// Hand-written types matching BE PR 9 schema until codegen can reach the
// staging BE. Derived from sso.model.ts, sso.resolver.ts, and Prisma enums.

import type { TypedDocumentNode } from "@graphql-typed-document-node/core"

// Until codegen generates the Organization type with tier/setupComplete,
// use this helper type for safe field access on org query results.
export type OrganizationWithEnterprise = {
  tier?: string | null
  setupComplete?: boolean | null
}

export enum AuthProvider {
  MAGIC_LINK = "MAGIC_LINK",
  GOOGLE = "GOOGLE",
  GITHUB = "GITHUB",
  MICROSOFT = "MICROSOFT",
  SAML = "SAML",
  OIDC = "OIDC",
}

export enum OrganizationTier {
  FREE = "FREE",
  BUSINESS = "BUSINESS",
  ENTERPRISE = "ENTERPRISE",
}

export enum AuthPolicyMode {
  OFF = "OFF",
  OPTIONAL = "OPTIONAL",
  REQUIRED = "REQUIRED",
}

export type OrganizationAuthMethod = {
  provider: AuthProvider
  enabled: boolean
  createdOn: string
  updatedOn: string
}

export type OrganizationAuthPolicy = {
  id: string
  ssoMode: AuthPolicyMode
  allowedFallbackProviders: AuthProvider[]
  createdOn: string
  updatedOn: string
}

export type OrganizationSamlConfig = {
  id: string
  samlEntryPoint: string
  samlIssuer: string
  samlCallbackUrl: string
  samlSignatureAlgo: string
  verified: boolean
  createdOn: string
  updatedOn: string
}

export type SamlConfigInput = {
  samlEntryPoint: string
  samlIssuer: string
  samlCert: string
  samlSignatureAlgo?: string
}

export type OidcConfigInput = {
  oidcIssuer: string
  oidcAuthUrl: string
  oidcTokenUrl: string
  oidcUserInfoUrl?: string
  oidcClientId: string
  oidcClientSecret: string
  oidcScopes?: string
}

export type OrganizationOidcConfig = {
  id: string
  oidcIssuer: string
  oidcAuthUrl: string
  oidcTokenUrl: string
  oidcUserInfoUrl?: string | null
  oidcClientId: string
  oidcCallbackUrl: string
  oidcScopes: string
  verified: boolean
  createdOn: string
  updatedOn: string
}

export type OrganizationEntitlements = {
  tier: OrganizationTier
  features: Record<string, boolean>
}

export type AuthMethodsByHostPayload = {
  ssoMode: AuthPolicyMode
  methods: AuthProvider[]
  samlEntryPoint?: string
  oidcInitiationUrl?: string
}

// Hand-written DocumentNodes — codegen will replace these once it can reach
// the staging BE schema. The .graphql source files are the canonical ops.

export type UpdateOrganizationAuthMethodMutation = {
  updateOrganizationAuthMethod: OrganizationAuthMethod
}

export type UpdateOrganizationAuthMethodVariables = {
  orgID: string
  provider: AuthProvider
  enabled: boolean
  samlConfig?: SamlConfigInput
  oidcConfig?: OidcConfigInput
}

export const UpdateOrganizationAuthMethodDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateOrganizationAuthMethod" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orgID" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "provider" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "AuthProvider" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "enabled" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "Boolean" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "samlConfig" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "SamlConfigInputType" },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "oidcConfig" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "OidcConfigInputType" },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateOrganizationAuthMethod" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "provider" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "provider" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "enabled" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "enabled" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "samlConfig" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "samlConfig" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "oidcConfig" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "oidcConfig" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "provider" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
                { kind: "Field", name: { kind: "Name", value: "updatedOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  UpdateOrganizationAuthMethodMutation,
  UpdateOrganizationAuthMethodVariables
>

export type UpdateOrganizationAuthPolicyMutation = {
  updateOrganizationAuthPolicy: OrganizationAuthPolicy
}

export type UpdateOrganizationAuthPolicyVariables = {
  orgID: string
  ssoMode: AuthPolicyMode
  allowedFallbackProviders: AuthProvider[]
}

export const UpdateOrganizationAuthPolicyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateOrganizationAuthPolicy" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orgID" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "ssoMode" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "AuthPolicyMode" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "allowedFallbackProviders" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "ListType",
              type: {
                kind: "NonNullType",
                type: {
                  kind: "NamedType",
                  name: { kind: "Name", value: "AuthProvider" },
                },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateOrganizationAuthPolicy" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "ssoMode" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "ssoMode" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "allowedFallbackProviders" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "allowedFallbackProviders" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "ssoMode" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "allowedFallbackProviders" },
                },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
                { kind: "Field", name: { kind: "Name", value: "updatedOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  UpdateOrganizationAuthPolicyMutation,
  UpdateOrganizationAuthPolicyVariables
>

// --- Org auth-config query documents (hand-written AST; .graphql sources are
// canonical). A small builder keeps the four near-identical org-scoped queries
// readable and free of repeated variable/argument transcription errors. ---

export type OrganizationConfigQueryVariables = {
  orgID: string
}

export type OrganizationAuthMethodsQuery = {
  organizationAuthMethods: OrganizationAuthMethod[]
}

export type OrganizationSamlConfigQuery = {
  organizationSamlConfig: OrganizationSamlConfig | null
}

export type OrganizationOidcConfigQuery = {
  organizationOidcConfig: OrganizationOidcConfig | null
}

export type OrganizationAuthPolicyQuery = {
  organizationAuthPolicy: OrganizationAuthPolicy | null
}

const orgIDVariableDefinitions = [
  {
    kind: "VariableDefinition",
    variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
    type: {
      kind: "NonNullType",
      type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
    },
  },
]

const orgIDArguments = [
  {
    kind: "Argument",
    name: { kind: "Name", value: "orgID" },
    value: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
  },
]

const buildOrgConfigQuery = (
  operationName: string,
  rootField: string,
  fields: string[]
) => ({
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: operationName },
      variableDefinitions: orgIDVariableDefinitions,
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: rootField },
            arguments: orgIDArguments,
            selectionSet: {
              kind: "SelectionSet",
              selections: fields.map((value) => ({
                kind: "Field",
                name: { kind: "Name", value },
              })),
            },
          },
        ],
      },
    },
  ],
})

export const OrganizationAuthMethodsDocument = buildOrgConfigQuery(
  "OrganizationAuthMethods",
  "organizationAuthMethods",
  ["provider", "enabled", "createdOn", "updatedOn"]
) as unknown as TypedDocumentNode<
  OrganizationAuthMethodsQuery,
  OrganizationConfigQueryVariables
>

export const OrganizationSamlConfigDocument = buildOrgConfigQuery(
  "OrganizationSamlConfig",
  "organizationSamlConfig",
  [
    "id",
    "samlEntryPoint",
    "samlIssuer",
    "samlCallbackUrl",
    "samlSignatureAlgo",
    "verified",
    "createdOn",
    "updatedOn",
  ]
) as unknown as TypedDocumentNode<
  OrganizationSamlConfigQuery,
  OrganizationConfigQueryVariables
>

export const OrganizationOidcConfigDocument = buildOrgConfigQuery(
  "OrganizationOidcConfig",
  "organizationOidcConfig",
  [
    "id",
    "oidcIssuer",
    "oidcAuthUrl",
    "oidcTokenUrl",
    "oidcUserInfoUrl",
    "oidcClientId",
    "oidcCallbackUrl",
    "oidcScopes",
    "verified",
    "createdOn",
    "updatedOn",
  ]
) as unknown as TypedDocumentNode<
  OrganizationOidcConfigQuery,
  OrganizationConfigQueryVariables
>

export const OrganizationAuthPolicyDocument = buildOrgConfigQuery(
  "OrganizationAuthPolicy",
  "organizationAuthPolicy",
  ["id", "ssoMode", "allowedFallbackProviders", "createdOn", "updatedOn"]
) as unknown as TypedDocumentNode<
  OrganizationAuthPolicyQuery,
  OrganizationConfigQueryVariables
>

// --- Business ⇄ Enterprise Plan Changes (Stripe-backed) --------------------

export enum TierChangeStatus {
  APPLIED = "APPLIED",
  PAYMENT_REQUIRED = "PAYMENT_REQUIRED",
  SCHEDULED = "SCHEDULED",
}

export type DowngradeImpact = {
  ssoMemberCount: number
  scimEnabled: boolean
  activeTokenCount: number
}

export type TierChangePreview = {
  targetTier: OrganizationTier
  effectiveAt: string
  amountDueNow: number
  nextPeriodAmount: number | null
  currency: string
  downgradeImpact: DowngradeImpact | null
}

export type TierChangeResult = {
  status: TierChangeStatus
  paymentURL: string | null
  effectiveAt: string | null
}

export type PendingTierChange = {
  tier: OrganizationTier
  effectiveAt: string
}

export type PreviewOrganizationTierChangeQuery = {
  previewOrganizationTierChange: TierChangePreview
}

export type PreviewOrganizationTierChangeVariables = {
  orgID: string
  targetTier: OrganizationTier
}

export const PreviewOrganizationTierChangeDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "PreviewOrganizationTierChange" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "targetTier" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "OrganizationTier" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "previewOrganizationTierChange" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "targetTier" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "targetTier" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "targetTier" } },
                { kind: "Field", name: { kind: "Name", value: "effectiveAt" } },
                { kind: "Field", name: { kind: "Name", value: "amountDueNow" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nextPeriodAmount" },
                },
                { kind: "Field", name: { kind: "Name", value: "currency" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "downgradeImpact" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "ssoMemberCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "scimEnabled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "activeTokenCount" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  PreviewOrganizationTierChangeQuery,
  PreviewOrganizationTierChangeVariables
>

export type ChangeOrganizationTierMutation = {
  changeOrganizationTier: TierChangeResult
}

export type ChangeOrganizationTierVariables = {
  orgID: string
  targetTier: OrganizationTier
}

export const ChangeOrganizationTierDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "ChangeOrganizationTier" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "targetTier" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "OrganizationTier" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "changeOrganizationTier" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "targetTier" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "targetTier" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "paymentURL" } },
                { kind: "Field", name: { kind: "Name", value: "effectiveAt" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  ChangeOrganizationTierMutation,
  ChangeOrganizationTierVariables
>

export type OrganizationPendingTierChangeQuery = {
  organizationPendingTierChange: PendingTierChange | null
}

export type OrganizationPendingTierChangeVariables = {
  orgID: string
}

export const OrganizationPendingTierChangeDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationPendingTierChange" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationPendingTierChange" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "tier" } },
                { kind: "Field", name: { kind: "Name", value: "effectiveAt" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  OrganizationPendingTierChangeQuery,
  OrganizationPendingTierChangeVariables
>

export type CancelScheduledTierChangeMutation = {
  cancelScheduledTierChange: boolean
}

export type CancelScheduledTierChangeVariables = {
  orgID: string
}

export const CancelScheduledTierChangeDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CancelScheduledTierChange" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "cancelScheduledTierChange" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  CancelScheduledTierChangeMutation,
  CancelScheduledTierChangeVariables
>

export type OrganizationTierChangedSubscription = {
  organizationTierChanged: OrganizationTier
}

export type OrganizationTierChangedVariables = {
  orgID: string
}

export const OrganizationTierChangedDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "subscription",
      name: { kind: "Name", value: "OrganizationTierChanged" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationTierChanged" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orgID" },
                },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  OrganizationTierChangedSubscription,
  OrganizationTierChangedVariables
>

// --- Organization Configs & Allowed Auth Providers (GCIP & Postgres backed) ---

export enum OrgConfigEnum {
  // Onboarding
  ONBOARDING_COMPLETED = "ONBOARDING_COMPLETED",

  // User provisioning
  IS_SCIM_ENABLED = "IS_SCIM_ENABLED",

  // Sign-in restrictions - written only through their own mutations
  ALLOW_DOMAIN_WHITELISTING = "ALLOW_DOMAIN_WHITELISTING",
  WHITELISTED_DOMAINS = "WHITELISTED_DOMAINS",

  // OAuth - unset credentials fall back to Hoppscotch's own OAuth apps
  GOOGLE_CLIENT_ID = "GOOGLE_CLIENT_ID",
  GOOGLE_CLIENT_SECRET = "GOOGLE_CLIENT_SECRET",
  GOOGLE_CLIENT_SECRET_SET = "GOOGLE_CLIENT_SECRET_SET",
  GITHUB_CLIENT_ID = "GITHUB_CLIENT_ID",
  GITHUB_CLIENT_SECRET = "GITHUB_CLIENT_SECRET",
  GITHUB_CLIENT_SECRET_SET = "GITHUB_CLIENT_SECRET_SET",
  MICROSOFT_CLIENT_ID = "MICROSOFT_CLIENT_ID",
  MICROSOFT_CLIENT_SECRET = "MICROSOFT_CLIENT_SECRET",
  MICROSOFT_CLIENT_SECRET_SET = "MICROSOFT_CLIENT_SECRET_SET",

  // SAML
  SAML_DISPLAY_NAME = "SAML_DISPLAY_NAME",
  SAML_IDP_ENTITY_ID = "SAML_IDP_ENTITY_ID",
  SAML_SSO_URL = "SAML_SSO_URL",
  SAML_CERTIFICATES = "SAML_CERTIFICATES",
  SAML_PROVIDER_ID = "SAML_PROVIDER_ID",
  SAML_RP_ENTITY_ID = "SAML_RP_ENTITY_ID",
  SAML_CALLBACK_URL = "SAML_CALLBACK_URL",

  // OIDC
  OIDC_DISPLAY_NAME = "OIDC_DISPLAY_NAME",
  OIDC_ISSUER = "OIDC_ISSUER",
  OIDC_CLIENT_ID = "OIDC_CLIENT_ID",
  OIDC_CLIENT_SECRET = "OIDC_CLIENT_SECRET",
  OIDC_USE_CODE_FLOW = "OIDC_USE_CODE_FLOW",
  OIDC_PROVIDER_ID = "OIDC_PROVIDER_ID",
}

export type OrgConfig = {
  name: OrgConfigEnum
  value: string
}

export type OrgConfigArgs = {
  name: OrgConfigEnum
  value: string
}

export enum ServiceStatus {
  ENABLE = "ENABLE",
  DISABLE = "DISABLE",
}

export type OrgAuthProviderStatus = {
  provider: AuthProvider
  status: ServiceStatus
}

export type EnableAndDisableOrgAuthArgs = {
  provider: AuthProvider
  status: ServiceStatus
}

// --- Organization Enterprise Banners ---

export enum BannerType {
  INFO = "INFO",
  WARNING = "WARNING",
  ERROR = "ERROR",
}

export enum BannerStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export type OrganizationBanner = {
  id: string
  orgID: string
  type: BannerType
  status: BannerStatus
  message: string
  createdOn: string
}

export const OrganizationConfigsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationConfigs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "names" } },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: { kind: "NamedType", name: { kind: "Name", value: "OrgConfigEnum" } },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationConfigs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "names" },
                value: { kind: "Variable", name: { kind: "Name", value: "names" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "value" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { organizationConfigs: OrgConfig[] },
  { names?: OrgConfigEnum[] }
>

export const OrganizationAllowedAuthProvidersDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationAllowedAuthProviders" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationAllowedAuthProviders" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "provider" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { organizationAllowedAuthProviders: OrgAuthProviderStatus[] },
  Record<string, never>
>

export const UpdateOrganizationConfigsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateOrganizationConfigs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "configs" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "ListType",
              type: {
                kind: "NonNullType",
                type: { kind: "NamedType", name: { kind: "Name", value: "OrgConfigArgs" } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateOrganizationConfigs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "configs" },
                value: { kind: "Variable", name: { kind: "Name", value: "configs" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "value" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { updateOrganizationConfigs: OrgConfig[] },
  { configs: OrgConfigArgs[] }
>

export const EnableAndDisableOrganizationAuthDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "EnableAndDisableOrganizationAuth" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "providerInfo" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "ListType",
              type: {
                kind: "NonNullType",
                type: { kind: "NamedType", name: { kind: "Name", value: "EnableAndDisableOrgAuthArgs" } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "enableAndDisableOrganizationAuth" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "providerInfo" },
                value: { kind: "Variable", name: { kind: "Name", value: "providerInfo" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "provider" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { enableAndDisableOrganizationAuth: OrgAuthProviderStatus[] },
  { providerInfo: EnableAndDisableOrgAuthArgs[] }
>

export const OrganizationActiveBannerDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationActiveBanner" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationActiveBanner" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "orgID" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "message" } },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { organizationActiveBanner: OrganizationBanner | null },
  { orgID: string }
>

export const OrganizationWorkingBannerDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationWorkingBanner" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationWorkingBanner" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "orgID" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "message" } },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { organizationWorkingBanner: OrganizationBanner | null },
  { orgID: string }
>

export const CreateOrganizationBannerDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CreateOrganizationBanner" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "type" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "BannerType" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "message" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "createOrganizationBanner" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "type" },
                value: { kind: "Variable", name: { kind: "Name", value: "type" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "message" },
                value: { kind: "Variable", name: { kind: "Name", value: "message" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "orgID" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "message" } },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { createOrganizationBanner: OrganizationBanner },
  { orgID: string; type: BannerType; message: string }
>

export const UpdateOrganizationBannerDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateOrganizationBanner" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "type" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "BannerType" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "message" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "status" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "BannerStatus" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateOrganizationBanner" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "orgID" },
                value: { kind: "Variable", name: { kind: "Name", value: "orgID" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: { kind: "Variable", name: { kind: "Name", value: "id" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "type" },
                value: { kind: "Variable", name: { kind: "Name", value: "type" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "message" },
                value: { kind: "Variable", name: { kind: "Name", value: "message" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "status" },
                value: { kind: "Variable", name: { kind: "Name", value: "status" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "orgID" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "message" } },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { updateOrganizationBanner: OrganizationBanner },
  { orgID: string; id: string; type: BannerType; message: string; status: BannerStatus }
>

// --- Organization Tokens & SCIM Provisioning (Enterprise) ---

export type OrganizationToken = {
  id: string
  label: string
  createdOn: string
  expiresOn: string | null
  lastUsedOn: string
}

export type CreateOrganizationTokenResponse = {
  token: string
  info: OrganizationToken
}

export type OrganizationScimSettings = {
  enabled: boolean
  baseUrl: string | null
}

export const OrganizationTokensDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationTokens" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "skip" } },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "take" } },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationTokens" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "skip" },
                value: { kind: "Variable", name: { kind: "Name", value: "skip" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "take" },
                value: { kind: "Variable", name: { kind: "Name", value: "take" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "createdOn" } },
                { kind: "Field", name: { kind: "Name", value: "expiresOn" } },
                { kind: "Field", name: { kind: "Name", value: "lastUsedOn" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { organizationTokens: OrganizationToken[] },
  { skip?: number; take?: number }
>

export const CreateOrganizationTokenDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CreateOrganizationToken" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "label" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "expiryInDays" } },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "createOrganizationToken" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "label" },
                value: { kind: "Variable", name: { kind: "Name", value: "label" } },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "expiryInDays" },
                value: { kind: "Variable", name: { kind: "Name", value: "expiryInDays" } },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "token" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "info" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "createdOn" } },
                      { kind: "Field", name: { kind: "Name", value: "expiresOn" } },
                      { kind: "Field", name: { kind: "Name", value: "lastUsedOn" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { createOrganizationToken: CreateOrganizationTokenResponse },
  { label: string; expiryInDays?: number | null }
>

export const RevokeOrganizationTokenDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RevokeOrganizationToken" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "revokeOrganizationToken" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: { kind: "Variable", name: { kind: "Name", value: "id" } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { revokeOrganizationToken: boolean },
  { id: string }
>

export const OrganizationScimSettingsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "OrganizationScimSettings" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "organizationScimSettings" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "baseUrl" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  { organizationScimSettings: OrganizationScimSettings },
  Record<string, never>
>

export type UpdateWhitelistedDomainsMutation = {
  updateWhitelistedDomains: string[]
}
export type UpdateWhitelistedDomainsMutationVariables = {
  domains: string[]
}

export const UpdateWhitelistedDomainsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateWhitelistedDomains" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "domains" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "ListType",
              type: {
                kind: "NonNullType",
                type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateWhitelistedDomains" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "domains" },
                value: { kind: "Variable", name: { kind: "Name", value: "domains" } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  UpdateWhitelistedDomainsMutation,
  UpdateWhitelistedDomainsMutationVariables
>

export type ToggleDomainWhitelistingMutation = {
  toggleDomainWhitelisting: boolean
}
export type ToggleDomainWhitelistingMutationVariables = {
  status: ServiceStatus
}

export const ToggleDomainWhitelistingDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "ToggleDomainWhitelisting" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "status" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ServiceStatus" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "toggleDomainWhitelisting" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "status" },
                value: { kind: "Variable", name: { kind: "Name", value: "status" } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as TypedDocumentNode<
  ToggleDomainWhitelistingMutation,
  ToggleDomainWhitelistingMutationVariables
>



